import { useCallback, useEffect, useState } from "react";

/**
 * A ~40-line path router. React Router is not a dependency and this app has
 * four routes — pulling in a router would be more surface than the whole
 * navigation layer. Uses the History API for clean URLs (/f/law) that match
 * the spec; SPA fallback (_redirects) is owned by the deploy config.
 */

export type Route =
  | { name: "catalog" }
  | { name: "field"; fieldId: string }
  | { name: "history" }
  | { name: "coverage" }
  | { name: "notfound"; path: string };

export function parsePath(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/") return { name: "catalog" };
  if (clean === "/history") return { name: "history" };
  if (clean === "/coverage") return { name: "coverage" };
  const field = /^\/f\/([^/]+)$/.exec(clean);
  if (field) return { name: "field", fieldId: decodeURIComponent(field[1]) };
  return { name: "notfound", path: clean };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case "catalog":
      return "/";
    case "field":
      return `/f/${encodeURIComponent(route.fieldId)}`;
    case "history":
      return "/history";
    case "coverage":
      return "/coverage";
    case "notfound":
      return route.path;
  }
}

interface NavState {
  route: Route;
  navigate: (route: Route) => void;
  back: () => void;
}

export function useRouter(): NavState {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: Route) => {
    const href = hrefFor(next);
    if (href !== window.location.pathname) {
      window.history.pushState({}, "", href);
    }
    setRoute(next);
    window.scrollTo({ top: 0 });
  }, []);

  const back = useCallback(() => window.history.back(), []);

  return { route, navigate, back };
}

/** Anchor that keeps real href semantics (open-in-new-tab, middle-click) while
 *  intercepting plain left-clicks for client-side nav. */
export function Link({
  to,
  navigate,
  className,
  children,
  ariaLabel,
}: {
  to: Route;
  navigate: (r: Route) => void;
  className?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}) {
  return (
    <a
      href={hrefFor(to)}
      aria-label={ariaLabel}
      className={className}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
          return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}
